import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Film, PenLine, Check, Layers } from "lucide-react";
import { toast } from "sonner";

const GOAL_COLOR = {
  TOF: "text-primary border-primary/40",
  MOF: "text-foreground border-foreground/40",
  BOF: "text-destructive border-destructive/40",
};

export default function WeeklyPlan() {
  const [plan, setPlan] = useState(null);
  const [gen, setGen] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/api/plans/weekly");
    setPlan(data);
    if (data && data.posts.length) setOpenIdx(0);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGen(true);
    toast.message("Writing your Weekly Revenue Content Plan…", { description: "60–120 seconds." });
    try {
      await runTask("/api/plans/generate", null, { intervalMs: 2500, maxMs: 240000 });
      await load();
      toast.success("Plan ready");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Generation failed"); }
    finally { setGen(false); }
  };

  const markFilmed = async (postId) => {
    await api.post(`/api/plans/post/${postId}/filmed`);
    await load();
    toast.success("Marked filmed");
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Weekly Revenue Content Plan</div>
            <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">
              {plan ? `Week of ${plan.week_of}` : "No plan yet"}
            </h1>
          </div>
          <Button data-testid="btn-generate-plan" onClick={generate} disabled={gen}>
            {gen ? <><Loader2 className="h-4 w-4 animate-spin"/> Writing…</> : <><Sparkles className="h-4 w-4"/> {plan ? "Regenerate this week" : "Build this week's plan"}</>}
          </Button>
        </div>
      </div>

      {!plan ? (
        <div className="p-10 text-muted-foreground">
          Click <span className="text-foreground font-semibold">Build this week's plan</span> — ContentOS will write 7 posts, one per day, with hooks, scripts, captions and CTAs.
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-4">
          {/* Left: day tiles */}
          <div className="col-span-12 lg:col-span-4 space-y-2" data-testid="plan-day-list">
            {plan.posts.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setOpenIdx(i)}
                data-testid={`plan-day-${p.day.toLowerCase()}`}
                className={`w-full text-left border rounded p-4 transition-colors duration-200 ${
                  openIdx === i ? "border-foreground bg-secondary" : "border-border bg-card hover:bg-secondary"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold tracking-tight">{p.day}</div>
                  <span className={`pillar-badge ${GOAL_COLOR[p.goal] || ""}`}>{p.goal}</span>
                </div>
                <div className="mt-2 text-sm line-clamp-2">{p.topic}</div>
                <div className="mt-2 text-xs text-muted-foreground">{p.platform} · {p.format} · {p.post_time}</div>
                {p.filmed && <div className="mt-2 text-xs text-primary flex items-center gap-1"><Check className="h-3 w-3"/> Filmed</div>}
              </button>
            ))}
          </div>

          {/* Right: post detail */}
          <div className="col-span-12 lg:col-span-8 border border-border rounded p-6 bg-card" data-testid="plan-post-detail">
            {plan.posts[openIdx] && (() => {
              const p = plan.posts[openIdx];
              return (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`pillar-badge ${GOAL_COLOR[p.goal] || ""}`}>{p.goal}</span>
                    <span className="pillar-badge">{p.day}</span>
                    <span className="pillar-badge">{p.platform}</span>
                    <span className="pillar-badge">{p.format}</span>
                    <span className="pillar-badge">{p.post_time}</span>
                    {p.filmed && <span className="pillar-badge text-primary border-primary/40">Filmed</span>}
                  </div>

                  <div className="mt-6">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Topic</div>
                    <div className="mt-1 font-display font-black text-3xl tracking-tighter leading-tight">{p.topic}</div>
                  </div>

                  <div className="mt-6 border-l-4 border-primary pl-4">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Hook</div>
                    <div className="mt-1 font-display font-bold text-xl leading-tight">“{p.hook}”</div>
                  </div>

                  <div className="mt-6">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Script</div>
                    <div className="mt-1 whitespace-pre-wrap leading-relaxed text-[15px]">{p.script}</div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">B-roll</div>
                      <div className="mt-1 text-sm whitespace-pre-wrap">{p.b_roll}</div>
                    </div>
                    <div>
                      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Caption</div>
                      <div className="mt-1 text-sm whitespace-pre-wrap">{p.caption}</div>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-border pt-6">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">CTA</div>
                    <div className="mt-1 font-semibold">{p.cta}</div>
                  </div>

                  <div className="mt-4 bg-secondary rounded p-4">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Strategic reason</div>
                    <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{p.strategic_reason}</div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    <Button data-testid={`btn-variants-${p.id}`} variant="outline" onClick={() => nav(`/app/variants/${p.id}?topic=${encodeURIComponent(p.topic)}&hook=${encodeURIComponent(p.hook)}&script=${encodeURIComponent(p.script)}`)}>
                      <PenLine className="h-4 w-4"/> Get hook variants
                    </Button>
                    <Button data-testid={`btn-repurpose-${p.id}`} variant="outline" onClick={() => nav(`/app/repurpose/${p.id}?topic=${encodeURIComponent(p.topic)}&hook=${encodeURIComponent(p.hook)}&script=${encodeURIComponent(p.script)}&caption=${encodeURIComponent(p.caption)}`)}>
                      <Layers className="h-4 w-4"/> Repurpose to 7 formats
                    </Button>
                    {!p.filmed && (
                      <Button data-testid={`btn-mark-filmed-${p.id}`} onClick={() => markFilmed(p.id)}>
                        <Film className="h-4 w-4"/> Mark as filmed
                      </Button>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
